/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import MUIDataTable from "mui-datatables";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HistoryIcon from "@mui/icons-material/History";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import VehicleInfoCell from "../Generic/VehicleInfoCell";
import { TaskModel } from "../../Models/Models";
import { Vocabulary } from "../../Utils/Vocabulary";
import { CacheContext } from "../../Context/cacheContext";
import {
  TASK_PRIORITY_STYLES,
  TASK_STATUS_COLUMN_CONFIG,
} from "../../Utils/tasksConstants";
import TasksBacklogFilters, {
  BacklogFilters,
  BACKLOG_FILTERS_DEFAULT,
} from "./TasksBacklogFilters";
import { buildUrl, toQueryParams } from "../../Utils/tasksUtils";
import { getData } from "../../Services/getData";
import { deleteData } from "../../Services/deleteData";
import { postData } from "../../Services/postData";
import { componentNames, tableLayoutOnMobile } from "../../Utils/Constants";
import withRole from "../../Utils/withRole";
import { urlEnum } from "../../Utils/urlEnum";
import styles from "../../Styles/tasksDashboard.module.css";
import formatDate, { europeanDateWithTime } from "../../Utils/formatDate";
import TaskForm from "./TaskForm";
import { LiveTimingChip } from "./TaskCard";
import FilePreview from "../../Utils/FilePreview";
import GenericModal from "../../Modal/GenericModal";
import { getMuiTheme } from "../../Utils/globalStyleTables";
import Grid from "@mui/material/Grid";

function formatMinutes(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

type TimingInfo = {
  label: string;
  urgency: "error" | "warning" | "default";
  icon: React.ReactElement;
  tooltip: string;
};

function getTimingInfo(task: TaskModel): TimingInfo | null {
  if (!task.estimated_duration) return null;

  const isCompleted = task.status === "completed";

  if (isCompleted) {
    if (task.is_late && task.time_spent_minutes != null) {
      const lateMin = task.time_spent_minutes - task.estimated_duration;
      return {
        label: `${formatMinutes(Math.max(0, lateMin))} ${Vocabulary.tasksTimeOverdue}`,
        urgency: "error",
        icon: <HistoryIcon style={{ fontSize: 13 }} />,
        tooltip: Vocabulary.tasksOverdueLateCompletion,
      };
    }
    return null;
  }

  if (!task.started_at) {
    if (!task.start_date) {
      return null;
    }
    const now = new Date();
    const startDate = new Date(task.start_date);
    if (startDate > now) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const diffDays = Math.round((startDay.getTime() - today.getTime()) / 86_400_000);
      const label =
        diffDays === 0 ? Vocabulary.tasksDueToday :
        diffDays === 1 ? Vocabulary.tasksDueTomorrow :
        `${Vocabulary.tasksStartsIn} ${diffDays}z`;
      return {
        label,
        urgency: "default",
        icon: <CalendarTodayIcon style={{ fontSize: 13 }} />,
        tooltip: "",
      };
    }
  }

  if (task.is_late) {
    const spentMin = task.time_spent_minutes ?? 0;
    const overdueMin = Math.max(0, spentMin - task.estimated_duration);
    return {
      label: `${formatMinutes(overdueMin)} ${Vocabulary.tasksTimeOverdue}`,
      urgency: "error",
      icon: <ErrorOutlineIcon style={{ fontSize: 13 }} />,
      tooltip: Vocabulary.tasksOverdue,
    };
  }

  const spentMin = task.time_spent_minutes ?? 0;
  const remainMin = Math.max(0, task.estimated_duration - spentMin);
  const pctLeft = remainMin / task.estimated_duration;
  return {
    label: `${formatMinutes(remainMin)} ${Vocabulary.tasksTimeLeft}`,
    urgency: pctLeft < 0.25 ? "error" : pctLeft < 0.5 ? "warning" : "default",
    icon: pctLeft < 0.5
      ? <AccessAlarmIcon style={{ fontSize: 13 }} />
      : <AccessTimeIcon style={{ fontSize: 13 }} />,
    tooltip: "",
  };
}


type Props = { canAssignTasks?: boolean };

function TasksBacklogComponent({ canAssignTasks }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = Number(searchParams.get("tab") ?? 0);

  const filters: BacklogFilters = {
    search: searchParams.get("search") ?? "",
    status: (searchParams.get("status") ?? "") as BacklogFilters["status"],
    priority: (searchParams.get("priority") ??
      "") as BacklogFilters["priority"],
    dateFrom: searchParams.get("dateFrom") ?? BACKLOG_FILTERS_DEFAULT.dateFrom,
    dateTo: searchParams.get("dateTo") ?? BACKLOG_FILTERS_DEFAULT.dateTo,
    overdue: searchParams.get("overdue") === "1",
    assigneeId: searchParams.get("assigneeId")
      ? Number(searchParams.get("assigneeId"))
      : "",
  };

  const [tasks, setTasks] = useState<TaskModel[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [assigneeIds, setAssigneeIds] = useState<number[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskModel | null>(null);
  const [taskToDelete, setTaskToDelete] = useState<TaskModel | null>(null);
  const [taskToDuplicate, setTaskToDuplicate] = useState<TaskModel | null>(
    null,
  );

  const cacheContext = useContext(CacheContext);
  const currentUserId = Number(localStorage.getItem("useId"));
  const allUsers: any[] = cacheContext.cache?.users ?? [];

  const setFilters = useCallback(
    (next: BacklogFilters) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          next.search ? p.set("search", next.search) : p.delete("search");
          next.status ? p.set("status", next.status) : p.delete("status");
          next.priority
            ? p.set("priority", next.priority)
            : p.delete("priority");
          next.dateFrom
            ? p.set("dateFrom", next.dateFrom)
            : p.delete("dateFrom");
          next.dateTo ? p.set("dateTo", next.dateTo) : p.delete("dateTo");
          next.overdue ? p.set("overdue", "1") : p.delete("overdue");
          next.assigneeId
            ? p.set("assigneeId", String(next.assigneeId))
            : p.delete("assigneeId");
          return p;
        },
        { replace: true },
      );
      setPage(0);
    },
    [setSearchParams],
  );

  const setActiveTab = useCallback(
    (tab: number) => {
      setSearchParams(
        () => {
          const p = new URLSearchParams();
          p.set("tab", String(tab));
          return p;
        },
        { replace: true },
      );
      setPage(0);
    },
    [setSearchParams],
  );

  const fetchTasks = useCallback(() => {
    const filterParam =
      activeTab === 0
        ? { assigneeId: String(currentUserId), createdById: "" }
        : {
            assigneeId: filters.assigneeId ? String(filters.assigneeId) : "",
            createdById: "",
          };

    getData(
      buildUrl(
        urlEnum.tasks,
        toQueryParams(
          filters.search,
          {
            period: filters.overdue ? "overdue" : "all",
            startDate: filters.dateFrom,
            endDate: filters.dateTo,
            priority: filters.priority,
            status: filters.status,
            ...filterParam,
          },
          { page, perPage: rowsPerPage },
        ),
      ),
    )
      .then((res: any) => {
        const raw = res?.data?.tasks ?? res?.data;
        setTasks(Array.isArray(raw) ? raw : []);
        setAssigneeIds(res?.data?.assigneeIds ?? []);
        setTotalCount(res?.data?.totalCount ?? 0);
      })
      .catch(console.error);
  }, [searchParams, page, rowsPerPage]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  function openEdit(task: TaskModel) {
    setSelectedTask(task);
    setFormOpen(true);
  }

  function closeForm() {
    setFormOpen(false);
    setSelectedTask(null);
  }

  function handleSave(values: TaskModel) {
    const isEditing = !!values.id;
    const request = isEditing
      ? postData(`${urlEnum.tasks}/${values.id}`, values)
      : postData(urlEnum.tasks, values);
    request
      .then(() => {
        closeForm();
        fetchTasks();
      })
      .catch(console.error);
  }

  function confirmDelete() {
    if (!taskToDelete?.id) return;
    deleteData(`${urlEnum.tasks}/${taskToDelete.id}`).then(() => {
      setTasks((prev) => prev.filter((t) => t.id !== taskToDelete.id));
      setTaskToDelete(null);
    });
  }

  function confirmDuplicate() {
    if (!taskToDuplicate) return;
    const { id: _id, ...rest } = taskToDuplicate as any;
    postData(urlEnum.tasks, rest).then(() => {
      setTaskToDuplicate(null);
      fetchTasks();
    });
  }

  function getUserName(userId: number | null): string {
    if (!userId) return "—";
    return (
      cacheContext.cache?.users?.find((u: any) => u.id === userId)?.nume ?? "—"
    );
  }

  function renderStatus(status: string) {
    const config = TASK_STATUS_COLUMN_CONFIG.find((c) => c.status === status);
    if (!config) return null;
    return (
      <span
        className={styles.backlogStatusChip}
        style={{ color: config.color, background: config.bgColor }}
      >
        <span
          className={styles.backlogStatusDot}
          style={{ background: config.accentColor }}
        />
        {config.label}
      </span>
    );
  }

  function renderPriority(priority: string | null) {
    if (!priority) return <span className={styles.backlogEmpty2}>—</span>;
    const s =
      TASK_PRIORITY_STYLES[priority as keyof typeof TASK_PRIORITY_STYLES];
    if (!s) return null;
    return (
      <span
        className={`${styles.cardPriorityChip} ${styles[s.chipClass as keyof typeof styles]}`}
      >
        {s.label}
      </span>
    );
  }

  function renderTiming(task: TaskModel) {
    if (task.status === "in_progress" && task.started_at) {
      return <LiveTimingChip task={task} />;
    }
    const info = getTimingInfo(task);
    if (!info) return <span className={styles.backlogEmpty2}>—</span>;
    return (
      <Tooltip title={info.tooltip} placement="top" arrow disableHoverListener={!info.tooltip}>
        <span className={`${styles.cardTiming} ${
          info.urgency === "error" ? styles.cardTimingError :
          info.urgency === "warning" ? styles.cardTimingWarning : ""
        }`}>
          {info.icon}
          {info.label}
        </span>
      </Tooltip>
    );
  }

  function renderVehicle(task: TaskModel) {
    if (!task.vehicle_info) return <span className={styles.backlogEmpty2}>—</span>;
    return (
      <VehicleInfoCell
        name={task.vehicle_info.name}
        vin={task.vehicle_info.vin}
        vehicleId={task.vehicle_info.id}
      />
    );
  }

  function RowActionsMenu({ task }: { task: TaskModel }) {
    const [anchor, setAnchor] = useState<null | HTMLElement>(null);
    return (
      <>
        <Tooltip title={Vocabulary.options} placement="top">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setAnchor(e.currentTarget);
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={() => setAnchor(null)}
          onClick={(e) => e.stopPropagation()}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          elevation={0}
          PaperProps={{ className: styles.rowMenuPaper }}
        >
          <MenuItem
            className={styles.rowMenuItem}
            onClick={() => {
              setAnchor(null);
              openEdit(task);
            }}
          >
            <ListItemIcon className={styles.rowMenuItemIcon}>
              <EditIcon sx={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText>{Vocabulary.edit}</ListItemText>
          </MenuItem>
          <MenuItem
            className={styles.rowMenuItem}
            onClick={() => {
              setAnchor(null);
              setTaskToDuplicate(task);
            }}
          >
            <ListItemIcon className={styles.rowMenuItemIcon}>
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText>{Vocabulary.duplicateVehicleButtonText}</ListItemText>
          </MenuItem>
          <MenuItem
            className={styles.rowMenuItemDanger}
            onClick={() => {
              setAnchor(null);
              setTaskToDelete(task);
            }}
          >
            <ListItemIcon className={styles.rowMenuItemIconDanger}>
              <DeleteIcon sx={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText>{Vocabulary.delete}</ListItemText>
          </MenuItem>
        </Menu>
      </>
    );
  }

  const baseColumns: any[] = [
    {
      name: "name",
      label: Vocabulary.taskName,
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (rowIndex: number) => {
          const task = tasks[rowIndex];
          const subtaskCount = task.subtasks?.length ?? 0;
          return (
            <div className={styles.backlogTdName}>
              <div className={styles.backlogTaskName}>{task.name}</div>
              {task.description && (
                <div className={styles.backlogTaskDesc}>{task.description}</div>
              )}
              {subtaskCount > 0 && (
                <div className={styles.backlogSubtaskToggle}>
                  {subtaskCount} subtask{subtaskCount !== 1 ? "-uri" : ""}
                </div>
              )}
            </div>
          );
        },
      },
    },
    {
      name: "status",
      label: Vocabulary.taskStatus,
      options: {
        sort: false,
        filter: false,
        customBodyRender: (value: string) => renderStatus(value),
      },
    },
    {
      name: "priority",
      label: Vocabulary.taskPriority,
      options: {
        sort: false,
        filter: false,
        customBodyRender: (value: string | null) => renderPriority(value),
      },
    },
    {
      name: "estimated_duration",
      label: Vocabulary.taskEstimatedDuration,
      options: {
        sort: false,
        filter: false,
        customBodyRender: (value: number | null) =>
          value != null ? (
            <span className={styles.backlogDueDate}>
              {formatMinutes(value)}
            </span>
          ) : (
            <span className={styles.backlogEmpty2}>—</span>
          ),
      },
    },
    {
      name: "started_at",
      label: Vocabulary.startedAt,
      options: {
        sort: false,
        filter: false,
        customBodyRender: (value: string | null) =>
          value ? (
            <span className={styles.backlogDueDate}>
              {formatDate(value, europeanDateWithTime)}
            </span>
          ) : (
            <span className={styles.backlogEmpty2}>—</span>
          ),
      },
    },
    {
      name: "completed_at",
      label: Vocabulary.completedAt,
      options: {
        sort: false,
        filter: false,
        customBodyRender: (value: string | null) =>
          value ? (
            <span className={styles.backlogDueDate}>
              {formatDate(value, europeanDateWithTime)}
            </span>
          ) : (
            <span className={styles.backlogEmpty2}>—</span>
          ),
      },
    },
    {
      name: "",
      label: Vocabulary.tasksOverdue,
      options: {
        sort: false,
        filter: false,
        download: false,
        empty: true,
        customBodyRenderLite: (rowIndex: number) =>
          renderTiming(tasks[rowIndex]),
      },
    },
    {
      name: "",
      label: Vocabulary.vehicle,
      options: {
        sort: false,
        filter: false,
        download: false,
        empty: true,
        customBodyRenderLite: (rowIndex: number) =>
          renderVehicle(tasks[rowIndex]),
      },
    },
    {
      name: "created_by_user_name",
      label: Vocabulary.createdBy,
      options: {
        sort: false,
        filter: false,
        customBodyRender: (value: string | null) =>
          value ? (
            <span className={styles.backlogDueDate}>
              <PersonOutlineIcon style={{ fontSize: 14, marginRight: 4, color: "#9ca3af" }} />
              {value}
            </span>
          ) : (
            <span className={styles.backlogEmpty2}>—</span>
          ),
      },
    },
    {
      name: "",
      label: "",
      options: {
        sort: false,
        filter: false,
        download: false,
        empty: true,
        setCellHeaderProps: () => ({ align: "right" }),
        setCellProps: () => ({ align: "right" }),
        customBodyRenderLite: (rowIndex: number) => (
          <RowActionsMenu task={tasks[rowIndex]} />
        ),
      },
    },
  ];

  const assigneeColumn = {
    name: "assigned_to_user_id",
    label: Vocabulary.taskResponsible,
    options: {
      sort: false,
      filter: false,
      customBodyRender: (value: number | null) => {
        const name = getUserName(value);
        return name !== "—" ? (
          <span className={styles.backlogDueDate}>
            <PersonOutlineIcon style={{ fontSize: 14, marginRight: 4, color: "#9ca3af" }} />
            {name}
          </span>
        ) : (
          <span className={styles.backlogEmpty2}>—</span>
        );
      },
    },
  };

  const columns =
    activeTab === 1
      ? [...baseColumns.slice(0, 3), assigneeColumn, ...baseColumns.slice(3)]
      : baseColumns;

  const tableOptions = {
    selectableRows: "none" as any,
    responsive: tableLayoutOnMobile,
    viewColumns: false,
    filter: false,
    print: false,
    download: false,
    search: false,
    sort: false,
    serverSide: true,
    count: totalCount,
    rowsPerPage,
    page,
    rowsPerPageOptions: [10, 25, 50],
    onChangePage: (p: number) => setPage(p),
    onChangeRowsPerPage: (s: number) => {
      setRowsPerPage(s);
      setPage(0);
    },
    onRowClick: (_: any, meta: any) => {
      openEdit(tasks[meta.dataIndex]);
    },
    textLabels: {
      body: { noMatch: Vocabulary.noTasks },
      pagination: Vocabulary.pagination,
    },
  };

  return (
    <>
      <div className={styles.boardHeader}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          disableElevation
          onClick={() => {
            setSelectedTask(new TaskModel());
            setFormOpen(true);
          }}
          style={{
            borderRadius: 8,
            textTransform: "none",
            fontWeight: 600,
            color: "#fff",
            whiteSpace: "nowrap",
            padding: "8px 32px",
          }}
        >
          {Vocabulary.taskNew}
        </Button>
      </div>

      <div className={styles.backlogContainer}>
        {canAssignTasks && (
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            className={styles.backlogTabs}
            TabIndicatorProps={{ style: { background: "#FF6633" } }}
          >
            <Tab label={Vocabulary.tasksAssignedToMe} />
            <Tab label={Vocabulary.tasksAll} />
          </Tabs>
        )}

        <div style={!canAssignTasks ? { marginTop: 16 } : undefined}>
          <TasksBacklogFilters
            filters={filters}
            onChange={setFilters}
            activeTab={activeTab}
            assigneeOptions={allUsers
              .filter((u) => assigneeIds.includes(u.id))
              .map((u) => ({ id: u.id, label: u.nume ?? u.name ?? "" }))}
          />
        </div>

        <ThemeProvider theme={getMuiTheme()}>
          <MUIDataTable
            title=""
            data={tasks}
            columns={columns}
            options={tableOptions}
          />
        </ThemeProvider>
      </div>

      {formOpen && (
        <TaskForm
          name={componentNames.TaskForm}
          open={formOpen}
          task={selectedTask}
          onClose={closeForm}
          onSave={handleSave}
          canAssignTasks={canAssignTasks}
        />
      )}

      <GenericModal
        open={!!taskToDelete}
        onClose={() => setTaskToDelete(null)}
        title={Vocabulary.taskDelete}
      >
        <p style={{ textAlign: "center", fontSize: 18 }}>
          {Vocabulary.taskDeleteMessage}
        </p>
        <Grid container spacing={2} style={{ padding: "8px 0" }}>
          <Grid item xs={6}>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              onClick={() => setTaskToDelete(null)}
            >
              {Vocabulary.no}
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              variant="contained"
              color="error"
              fullWidth
              onClick={confirmDelete}
            >
              {Vocabulary.yes}
            </Button>
          </Grid>
        </Grid>
      </GenericModal>

      <GenericModal
        open={!!taskToDuplicate}
        onClose={() => setTaskToDuplicate(null)}
        title={Vocabulary.taskDuplicate}
      >
        <p style={{ textAlign: "center", fontSize: 18 }}>
          {Vocabulary.taskDuplicateMessage}
        </p>
        <Grid container spacing={2} style={{ padding: "8px 0" }}>
          <Grid item xs={6}>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              onClick={() => setTaskToDuplicate(null)}
            >
              {Vocabulary.no}
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              style={{ color: "#fff" }}
              onClick={confirmDuplicate}
            >
              {Vocabulary.yes}
            </Button>
          </Grid>
        </Grid>
      </GenericModal>

      <FilePreview key={tasks.length} />
    </>
  );
}

export default withRole(TasksBacklogComponent);
